import { useSearchParams } from "react-router-dom";
import environment from "web/script/modules/environment";

export enum AdminPanelScenario {
    FORCED_HAPPY_PATH = "ap_forced_happy_path",
    SHIPPING_FORM_ERROR = "ap_shipping_form_error",
    SHIPPING_FORM_INVALID_ADDRESS_ERROR = "ap_shipping_form_invalid_address_error",
    SHIPPING_FORM_REFUSED_ADDRESS_ERROR = "ap_shipping_form_refused_address_error",
    DELIVERY_METHOD_ERROR = "ap_delivery_method_error",
    PAYMENT_METHOD_ERROR = "ap_payment_method_error",
    BUY_NOW_ERROR = "ap_buy_now_error",
    BUY_NOW_ERROR_PAYMENT = "ap_buy_now_error_payment",
    FORCED_THREE_DS_CHALLENGE = "ap_forced_three_ds_challenge",
    ORDER_FAILED_OOS = "ap_order_failed_oos",
    ORDER_PENDING = "ap_order_pending",
    RESET_ENV_FEATURES = "ap_reset_env_features",
}

export enum AdminPanelEnv {
    // TEMPORARY
    FORCED_HAPPY_PATH = "checkp_new_checkout_forced_happy_path_p1",
    SHIPPING_FORM_ERROR = "checkp_new_checkout_shipping_form_error_p1",
    SHIPPING_FORM_INVALID_ADDRESS_ERROR = "checkp_new_checkout_shipping_form_invalid_address_error_p1",
    SHIPPING_FORM_REFUSED_ADDRESS_ERROR = "checkp_new_checkout_shipping_form_refused_address_error_p1",
    DELIVERY_METHOD_ERROR = "checkp_new_checkout_delivery_method_error_p1",
    PAYMENT_METHOD_ERROR = "checkp_new_checkout_payment_method_error_p1",
    BUY_NOW_ERROR = "checkp_new_checkout_buy_now_error_p1",
    BUY_NOW_ERROR_PAYMENT = "checkp_new_checkout_buy_now_error_payment_p1",
    FORCED_THREE_DS_CHALLENGE = "checkp_new_checkout_forced_three_ds_challenge_p1",
    ORDER_FAILED_OOS = "checkp_new_checkout_order_failed_oos_p1",
    ORDER_PENDING = "checkp_new_checkout_order_pending_p1",
    INITIATE_ERROR = "checkp_new_checkout_initiate_error_p1",

    // PERMANENT
    AP_GUI = "checkp_new_checkout_ap_gui",
}

interface UseAdminPanel {
    scenario?: AdminPanelScenario;
}

export function timeout(ms: number): Promise<boolean> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

// TODO CHECKP-196 Flag Cleanup
function useAdminPanel(): UseAdminPanel {
    const [searchParams] = useSearchParams();

    const resetFeatures = searchParams.get(AdminPanelScenario.RESET_ENV_FEATURES);
    if (resetFeatures) {
        environment.delete(AdminPanelEnv.FORCED_HAPPY_PATH);
        environment.delete(AdminPanelEnv.SHIPPING_FORM_ERROR);
        environment.delete(AdminPanelEnv.SHIPPING_FORM_INVALID_ADDRESS_ERROR);
        environment.delete(AdminPanelEnv.DELIVERY_METHOD_ERROR);
        environment.delete(AdminPanelEnv.PAYMENT_METHOD_ERROR);
        environment.delete(AdminPanelEnv.BUY_NOW_ERROR);
        environment.delete(AdminPanelEnv.BUY_NOW_ERROR_PAYMENT);
        environment.delete(AdminPanelEnv.FORCED_THREE_DS_CHALLENGE);
        environment.delete(AdminPanelEnv.ORDER_FAILED_OOS);
    }

    const forcedHappyPath =
        searchParams.get(AdminPanelScenario.FORCED_HAPPY_PATH) === "1" ||
        environment.getFeature(AdminPanelEnv.FORCED_HAPPY_PATH);
    if (forcedHappyPath) {
        return { scenario: AdminPanelScenario.FORCED_HAPPY_PATH };
    }

    const shippingFormError =
        searchParams.get(AdminPanelScenario.SHIPPING_FORM_ERROR) === "1" ||
        environment.getFeature(AdminPanelEnv.SHIPPING_FORM_ERROR);
    if (shippingFormError) {
        return { scenario: AdminPanelScenario.SHIPPING_FORM_ERROR };
    }

    const shippingFormInvalidAddressError =
        searchParams.get(AdminPanelScenario.SHIPPING_FORM_INVALID_ADDRESS_ERROR) === "1" ||
        environment.getFeature(AdminPanelEnv.SHIPPING_FORM_INVALID_ADDRESS_ERROR);
    if (shippingFormInvalidAddressError) {
        return { scenario: AdminPanelScenario.SHIPPING_FORM_INVALID_ADDRESS_ERROR };
    }

    const shippingFormRefusedAddressError =
        searchParams.get(AdminPanelScenario.SHIPPING_FORM_REFUSED_ADDRESS_ERROR) === "1" ||
        environment.getFeature(AdminPanelEnv.SHIPPING_FORM_REFUSED_ADDRESS_ERROR);
    if (shippingFormRefusedAddressError) {
        return { scenario: AdminPanelScenario.SHIPPING_FORM_REFUSED_ADDRESS_ERROR };
    }

    const deliveryMethodError =
        searchParams.get(AdminPanelScenario.DELIVERY_METHOD_ERROR) === "1" ||
        environment.getFeature(AdminPanelEnv.DELIVERY_METHOD_ERROR);
    if (deliveryMethodError) {
        return { scenario: AdminPanelScenario.DELIVERY_METHOD_ERROR };
    }

    const paymentMethodError =
        searchParams.get(AdminPanelScenario.PAYMENT_METHOD_ERROR) === "1" ||
        environment.getFeature(AdminPanelEnv.PAYMENT_METHOD_ERROR);
    if (paymentMethodError) {
        return { scenario: AdminPanelScenario.PAYMENT_METHOD_ERROR };
    }

    const buyNowError =
        searchParams.get(AdminPanelScenario.BUY_NOW_ERROR) === "1" ||
        environment.getFeature(AdminPanelEnv.BUY_NOW_ERROR);
    if (buyNowError) {
        return { scenario: AdminPanelScenario.BUY_NOW_ERROR };
    }

    const buyNowErrorPayment =
        searchParams.get(AdminPanelScenario.BUY_NOW_ERROR_PAYMENT) === "1" ||
        environment.getFeature(AdminPanelEnv.BUY_NOW_ERROR_PAYMENT);
    if (buyNowErrorPayment) {
        return { scenario: AdminPanelScenario.BUY_NOW_ERROR_PAYMENT };
    }

    const forcedThreeDsChallenge =
        searchParams.get(AdminPanelScenario.FORCED_THREE_DS_CHALLENGE) === "1" ||
        environment.getFeature(AdminPanelEnv.FORCED_THREE_DS_CHALLENGE);
    if (forcedThreeDsChallenge) {
        return { scenario: AdminPanelScenario.FORCED_THREE_DS_CHALLENGE };
    }

    const orderFailedOos =
        searchParams.get(AdminPanelScenario.ORDER_FAILED_OOS) === "1" ||
        environment.getFeature(AdminPanelEnv.ORDER_FAILED_OOS);
    if (orderFailedOos) {
        return { scenario: AdminPanelScenario.ORDER_FAILED_OOS };
    }

    const orderPending =
        searchParams.get(AdminPanelScenario.ORDER_PENDING) === "1" ||
        environment.getFeature(AdminPanelEnv.ORDER_PENDING);
    if (orderPending) {
        return { scenario: AdminPanelScenario.ORDER_PENDING };
    }

    return {};
}

export default useAdminPanel;
